import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { CustomIcon, Header, Widget } from 'datastake-daf/dist/components';
import useStore from './context';
import Overview from './sections/Overview';
import Partners from './sections/Partners';
import Settings from './components/Settings';
import Triangulation from './sections/Triangulation';
import EntityWidget from './components/EntityWidget';
import DataStoreService from '../../services/DataStore';
import DataAccumulation from './sections/DataAccumulation';
import { renderBreadCrumbs } from '../../helpers/breadCrumbs';
import DashboardLayout from '../../components/dashboards/components/DashboardLayout';
import { Select } from 'antd';
import DataValue from './sections/DataValue';

const filterOptions = [
  { label: 'DataStore::data-points', value: 'datapoints' },
  { label: 'DataStore::data-subjects', value: 'subjects' },
]

export default function DataStore({ mod }) {
  const goTo = useNavigate();
  const { t } = useTranslation();
  const [analyticsFilter, setAnalyticsFilter] = useState('datapoints');
  const [infoSubject, setInfoSubject] = useState('informationSubjects');
  const { data, loading, fetchStore, setLoading } = useStore();
  const [settingsVisible, setSettingsVisible] = useState(false);

  const onFinish = async (formValue) => {
    setLoading(true);

    try {
      await DataStoreService.saveSubjectSettings(data.channel.id, formValue);
    } catch (err) {
      console.log(err);
    }
    
    setLoading(false);
  };

  useEffect(() => {
    fetchStore(false);
  }, []);

  const breadCrumbs = useMemo(() => {
    return renderBreadCrumbs({ t, goTo, mod, view: 'data-store' });
  }, [goTo, t, mod])

  return (
    <DashboardLayout>
      <Header
        breadcrumbs={breadCrumbs}
        title={t('Store')}
        actionButtons={[
          {
            label: (
              <div className="flex flex-column justify-content-center h-100 settings-icon">
                <CustomIcon name="Settings" width={16} height={16} />
              </div>
            ),
            onClick: () => setSettingsVisible(true),
          },
        ]}
      />

      <div className="content">
        <div className="view-content">
          <section>
            <Overview loading={loading} data={data.overview} />
            <Partners loading={loading} data={data.partners} mod={mod} />
          </section>

          <section>
            <Widget
              title={t('Details')}
              className="with-border-header h-w-btn-header flex-1"
              addedHeader={(
                <>
                  <div className="flex-1" />
                  <div>
                    <Select
                      value={infoSubject}
                      className='custom-select white'
                      onChange={(val) => setInfoSubject(val)}
                      style={{ minWidth: 175, maxWidth: 175 }}
                    >
                      <Select.Option value="informationSubjects">
                        {t('DataStore::data-subjects')}
                      </Select.Option>
                      <Select.Option value="infoDatapoints">
                        {t('DataStore::data-points')}
                      </Select.Option>
                    </Select>
                  </div>
                </>
              )}
            >
              <section>
                <EntityWidget
                  mod={mod}
                  loading={loading}
                  label="DataStore::entities"
                  namespace="entities"
                  infoSubject={infoSubject}
                  data={data.stakeholders}
                />
                <EntityWidget
                  mod={mod}
                  loading={loading}
                  label="Locations"
                  infoSubject={infoSubject}
                  namespace="locations"
                  data={data.locations}
                />
                <EntityWidget
                  mod={mod}
                  loading={loading}
                  label="Events"
                  infoSubject={infoSubject}
                  data={data.events}
                  namespace="events"
                />
                <EntityWidget
                  mod={mod}
                  loading={loading}
                  label="Archives"
                  infoSubject={infoSubject}
                  data={data.documents}
                  namespace="archives"
                />
              </section>
            </Widget>
          </section>

          <section>
            <Widget
              title={t('Analytics')}
              className="with-border-header h-w-btn-header flex-1"
              addedHeader={(
                <>
                  <div className="flex-1" />
                  <div>
                    <Select
                      value={analyticsFilter}
                      className='custom-select white'
                      onChange={(val) => setAnalyticsFilter(val)}
                      style={{ minWidth: 165, maxWidth: 165 }}
                    >
                      {filterOptions.map((o) => (
                        <Select.Option value={o.value} key={o.value}>
                          {t(o.label)}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </>
              )}
            >
              <section>
                <DataAccumulation
                  loading={loading}
                  filter={analyticsFilter}
                  data={data.accumulation}
                />
                <Triangulation loading={loading} data={data.tringulation} />
                <DataValue loading={loading} data={data} />
              </section>
            </Widget>
          </section>
        </div>
      </div>

      {data?.channel ? (
        <Settings
          onFinish={onFinish}
          supportText="Store"
          defaultData={{
            discoverability: data?.channel?.discoverability,
            accessibility: data?.channel?.accessibility,
            history: data?.channel?.history,
            stream: data?.channel?.stream,
            delivery: data?.channel?.delivery,
            licensing: data?.channel?.licensing,
          }}
          settingsVisible={settingsVisible}
          setSettingsVisible={setSettingsVisible}
        />
      ) : null}
    </DashboardLayout >
  )
}
